const ICON_IDS = new Set([
    "coin-2-fill",
    "hashtag-fill",
    "weight",
]);

export const CALCULATOR_METRIC_UNIT_MARK_NONE = Object.freeze({
    kind: "none",
});

export const CALCULATOR_METRIC_UNIT_MARK_PERCENT = Object.freeze({
    kind: "text",
    text: "%",
});

export const CALCULATOR_METRIC_UNIT_MARK_RAW_WEIGHT = Object.freeze({
    kind: "icon",
    iconId: "weight",
});

export const CALCULATOR_METRIC_UNIT_MARK_EXPECTED_COUNT = Object.freeze({
    kind: "icon",
    iconId: "hashtag-fill",
});

export const CALCULATOR_METRIC_UNIT_MARK_SILVER = Object.freeze({
    kind: "icon",
    iconId: "coin-2-fill",
});

export function calculatorMetricUnitMarkForValueField(field) {
    if (field === "expectedCount") {
        return CALCULATOR_METRIC_UNIT_MARK_EXPECTED_COUNT;
    }
    if (field === "expectedProfit") {
        return CALCULATOR_METRIC_UNIT_MARK_SILVER;
    }
    if (field === "expectedFishingExperience" || field === "expectedTotemExperience") {
        return CALCULATOR_METRIC_UNIT_MARK_NONE;
    }
    throw new TypeError(`unsupported calculator metric value field: ${field}`);
}

export function parseCalculatorMetricUnitMark(value, path = "unitMark") {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        throw new TypeError(`${path} must be an object`);
    }
    const fields = Object.keys(value).sort();
    if (value.kind === "none") {
        if (fields.length !== 1 || fields[0] !== "kind") {
            throw new TypeError(`${path} none mark must contain exactly kind`);
        }
        return CALCULATOR_METRIC_UNIT_MARK_NONE;
    }
    if (value.kind === "text") {
        if (
            fields.length !== 2
            || fields[0] !== "kind"
            || fields[1] !== "text"
            || value.text !== "%"
        ) {
            throw new TypeError(`${path} text mark must be the literal percent sign`);
        }
        return CALCULATOR_METRIC_UNIT_MARK_PERCENT;
    }
    if (value.kind === "icon") {
        if (
            fields.length !== 2
            || fields[0] !== "iconId"
            || fields[1] !== "kind"
            || !ICON_IDS.has(value.iconId)
        ) {
            throw new TypeError(`${path} icon mark must use an allowed metric icon`);
        }
        if (value.iconId === "weight") {
            return CALCULATOR_METRIC_UNIT_MARK_RAW_WEIGHT;
        }
        if (value.iconId === "hashtag-fill") {
            return CALCULATOR_METRIC_UNIT_MARK_EXPECTED_COUNT;
        }
        return CALCULATOR_METRIC_UNIT_MARK_SILVER;
    }
    throw new TypeError(`${path}.kind must be none, text, or icon`);
}

export function calculatorMetricTextWithUnit(display) {
    const text = String(display?.text ?? "");
    return display?.state === "known" && display?.unitMark?.kind === "text"
        ? `${text}${display.unitMark.text}`
        : text;
}

export function fitCalculatorMetricSvgText(
    value,
    {
        fontSize,
        maximumWidth,
        minimumFontSize = 8.5,
    },
) {
    const text = String(value ?? "");
    const baseFontSize = Number.parseFloat(fontSize);
    if (
        !Number.isFinite(baseFontSize)
        || baseFontSize <= 0
        || !Number.isFinite(maximumWidth)
        || maximumWidth <= 0
        || !Number.isFinite(minimumFontSize)
        || minimumFontSize <= 0
        || minimumFontSize > baseFontSize
    ) {
        throw new TypeError("metric SVG text fitting requires valid positive dimensions");
    }
    const estimatedWidth = [...text].length * baseFontSize * 0.56;
    if (estimatedWidth <= maximumWidth) {
        return Object.freeze({ text, fontSize: `${baseFontSize}px` });
    }
    const fittedFontSize = baseFontSize * maximumWidth / estimatedWidth;
    if (fittedFontSize >= minimumFontSize) {
        return Object.freeze({ text, fontSize: `${fittedFontSize}px` });
    }
    const maximumCharacters = Math.max(
        2,
        Math.floor(maximumWidth / (minimumFontSize * 0.56)),
    );
    const fittedText = text.length > maximumCharacters
        ? `${text.slice(0, maximumCharacters - 1)}…`
        : text;
    return Object.freeze({
        text: fittedText,
        fontSize: `${minimumFontSize}px`,
    });
}

export function calculatorMixedMetricLayout({ x, width }) {
    if (
        !Number.isFinite(x)
        || !Number.isFinite(width)
        || width <= 38
    ) {
        throw new TypeError("mixed metric layout requires a finite x and width above 38");
    }
    const contentLeft = x + 32;
    const contentRight = x + width - 6;
    return Object.freeze({
        watermarkX: x + 16,
        contentX: (contentLeft + contentRight) / 2,
        contentWidth: contentRight - contentLeft,
    });
}

export function appendCalculatorMetricWatermark(
    selection,
    {
        unitMark,
        x,
        y,
        color,
        size = 26,
        opacity = 0.1,
    },
) {
    const mark = parseCalculatorMetricUnitMark(unitMark);
    if (mark.kind === "none") {
        return;
    }
    if (mark.kind === "icon") {
        selection.append("use")
            .attr("href", `/img/icons.svg#fishy-${mark.iconId}`)
            .attr("x", x - size / 2)
            .attr("y", y - size / 2)
            .attr("width", size)
            .attr("height", size)
            .attr("aria-hidden", "true")
            .attr("data-calculator-metric-watermark", mark.iconId)
            .style("color", color)
            .style("fill", "currentColor")
            .style("opacity", opacity)
            .style("pointer-events", "none");
        return;
    }
    selection.append("text")
        .attr("x", x)
        .attr("y", y)
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "middle")
        .attr("aria-hidden", "true")
        .attr("data-calculator-metric-watermark", "percent")
        .style("fill", color)
        .style("font-size", `${size}px`)
        .style("font-weight", "800")
        .style("opacity", opacity)
        .style("pointer-events", "none")
        .text(mark.text);
}

export function appendCalculatorMetricDivider(
    selection,
    {
        x,
        y,
        width,
        color,
        inset = 12,
    },
) {
    selection.append("line")
        .attr("x1", x + inset)
        .attr("x2", x + width - inset)
        .attr("y1", y)
        .attr("y2", y)
        .attr("aria-hidden", "true")
        .attr("data-calculator-metric-divider", "")
        .style("stroke", color)
        .style("stroke-width", 1)
        .style("stroke-opacity", 0.2)
        .style("pointer-events", "none");
}

export function appendCalculatorMixedMetricDecorations(
    selection,
    {
        x,
        y,
        width,
        height,
        color,
        topUnitMark,
        bottomUnitMark,
    },
) {
    appendCalculatorMetricDivider(selection, {
        x,
        y: y + height / 2,
        width,
        color,
    });
    const { watermarkX } = calculatorMixedMetricLayout({ x, width });
    appendCalculatorMetricWatermark(selection, {
        unitMark: topUnitMark,
        x: watermarkX,
        y: y + height * 0.25,
        color,
    });
    appendCalculatorMetricWatermark(selection, {
        unitMark: bottomUnitMark,
        x: watermarkX,
        y: y + height * 0.75,
        color,
    });
}
